import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { 
  ArrowLeft, 
  CheckCircle, 
  Users, 
  Briefcase, 
  Award,
  X,
  Plus,
  Sparkles,
  Upload,
  FileText,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import WebsiteFooter from '@/components/website/WebsiteFooter';

const categories = [
  { value: 'ai_integration_engineer', label: 'AI Integration Engineer' },
  { value: 'ai_application_developer', label: 'AI Application Developer' },
  { value: 'ai_automation_engineer', label: 'AI Automation Engineer' },
  { value: 'prompt_engineer', label: 'Prompt Engineer' },
  { value: 'ai_workflow_designer', label: 'AI Workflow Designer' },
  { value: 'ai_qa_engineer', label: 'AI QA Engineer (AI Testing Engineer)' },
  { value: 'ai_validation_engineer', label: 'AI Validation Engineer' },
  { value: 'ai_data_engineer', label: 'AI Data Engineer' },
  { value: 'ai_rag_engineer', label: 'AI RAG Engineer' },
  { value: 'ai_devops_engineer', label: 'AI DevOps Engineer' },
  { value: 'mlops_engineer', label: 'MLOps Engineer' },
  { value: 'ai_platform_engineer', label: 'AI Platform Engineer' },
  { value: 'ai_solutions_engineer', label: 'AI Solutions Engineer' },
  { value: 'ai_support_engineer', label: 'AI Support Engineer' },
  { value: 'ai_chatbot_engineer', label: 'AI Chatbot Engineer' },
  { value: 'ai_copilot_engineer', label: 'AI Copilot Engineer' },
  { value: 'ai_api_engineer', label: 'AI API Engineer' },
  { value: 'ai_security_engineer', label: 'AI Security Engineer' },
  { value: 'ai_governance_engineer', label: 'AI Governance Engineer' },
  { value: 'ai_compliance_analyst', label: 'AI Compliance Analyst' },
  { value: 'ai_risk_analyst', label: 'AI Risk Analyst' },
  { value: 'ai_model_monitoring_engineer', label: 'AI Model Monitoring Engineer' },
  { value: 'ai_performance_engineer', label: 'AI Performance Engineer' },
  { value: 'ai_business_analyst', label: 'AI Business Analyst' },
  { value: 'ai_product_engineer', label: 'AI Product Engineer' },
  { value: 'ai_implementation_consultant', label: 'AI Implementation Consultant' },
  { value: 'ai_migration_engineer', label: 'AI Migration Engineer' },
  { value: 'ai_knowledge_systems_engineer', label: 'AI Knowledge Systems Engineer' },
];

const expertiseLevels = [
  { value: 'junior', label: 'Junior (0-2 years)' },
  { value: 'mid', label: 'Mid-Level (2-5 years)' },
  { value: 'senior', label: 'Senior (5-8 years)' },
  { value: 'lead', label: 'Lead (8-12 years)' },
  { value: 'principal', label: 'Principal (12+ years)' },
];

export default function JoinTalentPool() {
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showNonAIWarning, setShowNonAIWarning] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [certInput, setCertInput] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  
  const [formData, setFormData] = useState({
    display_name: '',
    title: '',
    category: '',
    bio: '',
    skills: [],
    years_of_experience: 0,
    expertise_level: 'mid',
    certifications: [],
    industries: [],
    email: '',
    phone: '',
    linkedin_url: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setShowConfirmation(false);
    if (name === 'category') {
      setCategoryError('');
      setShowNonAIWarning(false);
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setShowConfirmation(false);
    if (name === 'category') {
      setCategoryError('');
      setShowNonAIWarning(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const allowedExtensions = ['.pdf', '.docx', '.txt'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast.error('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setResumeFile(file);
    setIsAnalyzing(true);
    setCategoryError('');
    setShowNonAIWarning(false);

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/talents/analyze-resume`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout
      });

      const analysisResult = response.data;

      // Check if resume matches AI expertise
      if (!analysisResult.is_ai_professional || !analysisResult.category) {
        // Non-AI resume detected
        setShowNonAIWarning(true);
        setCategoryError(analysisResult.error_message || 'This resume does not appear to match AI/ML expertise. Please select a specialization manually.');
        
        // Still update other fields but leave category blank
        setFormData(prev => ({
          ...prev,
          display_name: analysisResult.display_name || prev.display_name,
          title: analysisResult.title || prev.title,
          category: '', // Leave blank for non-AI resumes
          bio: analysisResult.bio || prev.bio,
          skills: analysisResult.skills?.length > 0 ? analysisResult.skills : prev.skills,
          years_of_experience: analysisResult.years_of_experience || prev.years_of_experience,
          expertise_level: analysisResult.expertise_level || prev.expertise_level,
          certifications: analysisResult.certifications?.length > 0 ? analysisResult.certifications : prev.certifications,
          industries: analysisResult.industries?.length > 0 ? analysisResult.industries : prev.industries,
          email: analysisResult.email || prev.email,
          phone: analysisResult.phone || prev.phone,
          linkedin_url: analysisResult.linkedin_url || prev.linkedin_url,
        }));

        setShowConfirmation(true);
        toast.warning('Resume analyzed, but no AI expertise detected. Please select a specialization manually.');
      } else {
        // AI resume - update all fields including category
        setFormData(prev => ({
          ...prev,
          display_name: analysisResult.display_name || prev.display_name,
          title: analysisResult.title || prev.title,
          category: analysisResult.category || prev.category,
          bio: analysisResult.bio || prev.bio,
          skills: analysisResult.skills?.length > 0 ? analysisResult.skills : prev.skills,
          years_of_experience: analysisResult.years_of_experience || prev.years_of_experience,
          expertise_level: analysisResult.expertise_level || prev.expertise_level,
          certifications: analysisResult.certifications?.length > 0 ? analysisResult.certifications : prev.certifications,
          industries: analysisResult.industries?.length > 0 ? analysisResult.industries : prev.industries,
          email: analysisResult.email || prev.email,
          phone: analysisResult.phone || prev.phone,
          linkedin_url: analysisResult.linkedin_url || prev.linkedin_url,
        }));

        setShowConfirmation(true);
        toast.success('Resume analyzed successfully! Please review the extracted information.');
      }

    } catch (error) {
      console.error('Resume analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze resume. Please fill the form manually.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({ ...prev, skills: [...prev.skills, skillInput.trim()] }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addCertification = () => {
    if (certInput.trim() && !formData.certifications.includes(certInput.trim())) {
      setFormData(prev => ({ ...prev, certifications: [...prev.certifications, certInput.trim()] }));
      setCertInput('');
    }
  };

  const removeCertification = (cert) => {
    setFormData(prev => ({ ...prev, certifications: prev.certifications.filter(c => c !== cert) }));
  };

  const addIndustry = () => {
    if (industryInput.trim() && !formData.industries.includes(industryInput.trim())) {
      setFormData(prev => ({ ...prev, industries: [...prev.industries, industryInput.trim()] }));
      setIndustryInput('');
    }
  };

  const removeIndustry = (industry) => {
    setFormData(prev => ({ ...prev, industries: prev.industries.filter(i => i !== industry) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.display_name || !formData.title || !formData.bio || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.category) {
      setCategoryError('Please select a specialization');
      toast.error('Please select a specialization');
      return;
    }

    if (formData.skills.length === 0) {
      toast.error('Please add at least one skill');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/talents/submit`, {
        ...formData,
        years_of_experience: parseInt(formData.years_of_experience) || 0,
      });
      setSubmitted(true);
      toast.success('Your profile has been submitted for review!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-6">
              Your profile has been submitted successfully. Our team will review your application and get back to you soon.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/ai-talent">
                <Button variant="outline">
                  <Users className="w-4 h-4 mr-2" />
                  View Talent Pool
                </Button>
              </Link>
              <Link to="/">
                <Button>
                  Back to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png" 
              alt="Aurous" 
              className="h-8 w-auto"
            />
          </Link>
          <Link to="/ai-talent">
            <Button variant="ghost" className="text-slate-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Talent Pool
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <div className="py-12 px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2 mb-6">
          <Sparkles className="w-4 h-4 text-blue-400" />
          <span className="text-blue-400 text-sm font-medium">Join Our AI Talent Network</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Submit Your Profile
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto">
          Join our curated network of AI professionals. Get discovered by top companies looking for specialized talent.
        </p>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 pb-12">
        <form onSubmit={handleSubmit}>
          {/* Resume Upload Card */}
          <Card className="border-0 shadow-xl mb-6 bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Upload className="w-5 h-5 text-blue-400" />
                Quick Fill with Resume
              </CardTitle>
              <CardDescription className="text-slate-300">
                Upload your resume and let AI auto-fill the form for you
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleResumeUpload}
                  className="hidden"
                  data-testid="resume-upload-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isAnalyzing}
                  className="w-full max-w-md border-dashed border-2 border-blue-500/50 bg-blue-500/5 hover:bg-blue-500/10 text-white h-24"
                  data-testid="resume-upload-btn"
                >
                  {isAnalyzing ? (
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                      <span>Analyzing your resume...</span>
                    </div>
                  ) : resumeFile ? (
                    <div className="flex items-center gap-3">
                      <FileText className="w-6 h-6 text-green-400" />
                      <div className="text-left">
                        <p className="font-medium">{resumeFile.name}</p>
                        <p className="text-sm text-slate-400">Click to upload a different file</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Upload className="w-6 h-6 text-blue-400" />
                      <div className="text-left">
                        <p className="font-medium">Upload Resume</p>
                        <p className="text-sm text-slate-400">PDF, DOCX, or TXT (max 5MB)</p>
                      </div>
                    </div>
                  )}
                </Button>
                <p className="text-xs text-slate-400">
                  Your resume will be analyzed by AI to extract relevant information
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Confirmation Alert */}
          {showConfirmation && !showNonAIWarning && (
            <Alert className="mb-6 bg-amber-500/10 border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-200">
                <strong>Please review all fields carefully!</strong> AI has auto-filled your information from the resume. 
                Verify and correct any inaccuracies before submitting.
              </AlertDescription>
            </Alert>
          )}

          {/* Non-AI Resume Warning */}
          {showNonAIWarning && (
            <Alert className="mb-6 bg-red-500/10 border-red-500/30">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-red-200">
                <strong>No AI expertise detected!</strong> Your resume does not appear to match AI/ML specializations. 
                Other fields have been filled, but please <strong>select a specialization manually</strong> if you believe you have relevant AI experience.
              </AlertDescription>
            </Alert>
          )}

          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Professional Information
              </CardTitle>
              <CardDescription>Tell us about your professional background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name *</Label>
                  <Input
                    id="display_name"
                    name="display_name"
                    placeholder="e.g., John D."
                    value={formData.display_name}
                    onChange={handleChange}
                    required
                    data-testid="display-name-input"
                  />
                  <p className="text-xs text-slate-500">Use first name + initial for privacy</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">Professional Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    placeholder="e.g., Senior AI Engineer"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    data-testid="title-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={categoryError ? 'text-red-500' : ''}>Specialization *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => handleSelectChange('category', v)}
                  >
                    <SelectTrigger 
                      data-testid="category-select"
                      className={categoryError ? 'border-red-500 ring-red-500 focus:ring-red-500' : ''}
                    >
                      <SelectValue placeholder="Select a specialization..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {categories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {categoryError && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {categoryError}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Experience Level *</Label>
                  <Select 
                    value={formData.expertise_level} 
                    onValueChange={(v) => handleSelectChange('expertise_level', v)}
                  >
                    <SelectTrigger data-testid="expertise-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {expertiseLevels.map(level => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="years_of_experience">Years of Experience</Label>
                <Input
                  id="years_of_experience"
                  name="years_of_experience"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="5"
                  value={formData.years_of_experience}
                  onChange={handleChange}
                  className="w-32"
                  data-testid="experience-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Professional Summary *</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Describe your expertise, achievements, and what makes you unique..."
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="bio-input"
                />
                <p className="text-xs text-slate-500">Max 500 characters</p>
              </div>

              {/* Skills */}
              <div className="space-y-2">
                <Label>Skills *</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., Python, TensorFlow, LangChain..."
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    data-testid="skill-input"
                  />
                  <Button type="button" variant="outline" onClick={addSkill}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Certifications */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Certifications
                </Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., AWS ML Specialty, Google Cloud ML..."
                    value={certInput}
                    onChange={(e) => setCertInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                    data-testid="cert-input"
                  />
                  <Button type="button" variant="outline" onClick={addCertification}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.certifications.map((cert, idx) => (
                      <Badge key={idx} variant="outline" className="gap-1">
                        {cert}
                        <button type="button" onClick={() => removeCertification(cert)}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Industries */}
              <div className="space-y-2">
                <Label>Industries Worked In</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., Fintech, Healthcare, E-commerce..."
                    value={industryInput}
                    onChange={(e) => setIndustryInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIndustry())}
                    data-testid="industry-input"
                  />
                  <Button type="button" variant="outline" onClick={addIndustry}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.industries.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.industries.map((industry, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        {industry}
                        <button type="button" onClick={() => removeIndustry(industry)}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact Info Card */}
          <Card className="border-0 shadow-xl mt-6">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>How can companies reach you? (Only visible to admins)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1 555-0100"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkedin_url">LinkedIn Profile URL</Label>
                <Input
                  id="linkedin_url"
                  name="linkedin_url"
                  type="url"
                  placeholder="https://linkedin.com/in/yourprofile"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  data-testid="linkedin-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-end">
            <Link to="/ai-talent">
              <Button variant="outline" type="button" className="w-full sm:w-auto">
                Cancel
              </Button>
            </Link>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
              data-testid="submit-profile-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Profile
                </>
              )}
            </Button>
          </div>

          <p className="text-center text-slate-500 text-sm mt-4">
            By submitting, you agree to our terms and privacy policy. Your contact information will only be shared with verified companies.
          </p>
        </form>
      </div>
      
      <WebsiteFooter />
    </div>
  );
}
